import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExport from 'highcharts/modules/export-data';
import HighchartsExporting from 'highcharts/modules/exporting';
import { Content, Table, Icon, Input, InputGroup } from 'rsuite';
import { LoadingMenu, LoadingSpinerLocal } from '../../../../../../components/LoadingSpinner';
import { HighchartsOptions } from '../../../../../../utils/HighchartsConfig';
import { Select } from '../../../AderenciaPrecos/components';
import { FILTER_LOCALE } from '../../../AderenciaPrecos/data';
import { useAnaliseCoberturaStore } from '../../AnaliseCobertura.store';
import { formatterYLabel, seriesColors } from '../../utils';
import styles from './ProductsCoverageChart.module.scss';
import { CategoryResponse, Filter, SectionResponse } from '../../types';
import { COVERAGE_PRODUCT_TYPE_KEY, PRODUCT_COVERAGE_CHART_DATA } from '../../data';

const { Pagination } = Table;

HighchartsExporting(Highcharts);
HighchartsExport(Highcharts);
Highcharts.setOptions(HighchartsOptions);

type ProductsCoverageChartProps = {
  name: string;
  data?: CategoryResponse;
  loading?: boolean;
  onSearch?: (value: string) => void;
  onChangePage?: (page: number) => void;
  onClose?: () => void;
  onSelect?: (value: string, item: Filter) => void;
  onClean?: () => void;
  onOpen?: () => void;
};

const renderMenu = (name: string, menu: React.ReactNode) => (
  <span>
    <LoadingMenu area={`coverage-analysis-${name}`} />
    {menu}
  </span>
);

export const ProductsCoverageChart = ({ name, data, loading, onSearch, onChangePage, onClose, onSelect, onClean, onOpen }: ProductsCoverageChartProps) => {
  const coverage = useAnaliseCoberturaStore();

  const PLACEHOLDER = coverage[name].value.length ? PRODUCT_COVERAGE_CHART_DATA[name].selectedPlaceholder : PRODUCT_COVERAGE_CHART_DATA[name].placeholder;
  const LIST = coverage[name].list;
  const VALUE = coverage[name].value;
  const FILTER_LABEL = PRODUCT_COVERAGE_CHART_DATA[name].filterLabel;
  const TITLE = PRODUCT_COVERAGE_CHART_DATA[name].title;
  const X_AXIS_TITLE = PRODUCT_COVERAGE_CHART_DATA[name].xAxisTitle;

  const mapCategories = (data: SectionResponse) => {
    return data?.content?.map(item => item[COVERAGE_PRODUCT_TYPE_KEY[name]]);
  };

  const mapDataSeries = (data: SectionResponse) => {
    const dataSeries = {
      data: data?.content?.map((item, index) => ({
        name: item[COVERAGE_PRODUCT_TYPE_KEY[name]],
        y: item.cobertura,
        color: seriesColors[index],
      })),
    };

    return dataSeries;
  };

  const topPricesChart = {
    chart: {
      type: 'bar',
      height: 500,
    },
    title: {
      text: '',
    },
    legend: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
      formatter: (props: any) => {
        return `
                    ${props.chart?.hoverPoint?.name}: <b>${props.chart?.hoverPoint?.y}%</b>
                `;
      },
    },
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: true,
          format: '{y:.2f}%', // percentage value of each bar
        },
      },
    },
    yAxis: {
      breakSize: 1,
      alignTicks: false,
      title: {
        text: 'COBERTURA',
      },
      labels: {
        // names of the percentages on the horizontal line
        formatter: (props: any) => formatterYLabel(props),
      },
    },
    xAxis: {
      title: {
        text: X_AXIS_TITLE,
      },
      categories: data?.content?.length ? mapCategories(data) : [],
      labels: {
        // names on the vertical line / left side
        formatter: (props: any) => props.value,
      },
    },
    series: data?.content?.length ? mapDataSeries(data) : [],
  };

  return (
    <Content className={styles.container}>
      <LoadingSpinerLocal loading={loading} size="md" />
      <div className={styles.header}>
        <div className={styles['title-container']}>
          <h6 className={styles.title}>{TITLE}</h6>
          {name === 'city' ? (
            <InputGroup inside className={styles['search-container']}>
              <InputGroup.Addon>
                <Icon icon="search" className={styles['search-icon']} />
              </InputGroup.Addon>
              <Input className={styles['search-input']} placeholder={PLACEHOLDER} onChange={onSearch} />
            </InputGroup>
          ) : null}
        </div>
        {name !== 'city' ? (
          <Select
            className={styles.select}
            label={FILTER_LABEL}
            placeholder={PLACEHOLDER}
            value={VALUE}
            data={LIST}
            searchable
            onSelect={onSelect}
            onClean={onClean}
            onClose={onClose}
            onOpen={onOpen}
            onSearch={onSearch}
            locale={FILTER_LOCALE}
            renderMenu={menu => renderMenu(name, menu)}
          />
        ) : null}
      </div>

      <HighchartsReact highcharts={Highcharts} options={topPricesChart} />
      <Pagination activePage={data?.page?.number + 1} total={data?.page?.totalElements} displayLength={10} showLengthMenu={false} onChangePage={onChangePage} />
    </Content>
  );
};
